<template>
    <div>
        <LabelField :label="$t('totp_status_label')">
            <div class="d-flex align-center flex-wrap" style="gap: 12px">
                <span>{{ statusText }}</span>
                <PlainBtn v-if="isConfigured" :disabled="disabled" @click="openDialog">
                    {{ $t('totp_reset') }}
                </PlainBtn>
            </div>
        </LabelField>

        <v-dialog v-model="showDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title>{{ $t('totp_reset_title') }}</v-card-title>
                <v-card-text>
                    <p>{{ selfService ? $t('totp_reset_own_text') : $t('totp_reset_admin_text') }}</p>
                    <v-text-field
                        v-if="selfService"
                        v-model="password"
                        type="password"
                        :label="$t('current_pw')"
                        dense
                        outlined
                        autocomplete="current-password"
                        hide-details="auto"
                        @keyup.enter="submit"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <PlainBtn :disabled="loading" @click="showDialog = false">{{ $t('cancel') }}</PlainBtn>
                    <ActionBtn :loading="loading" :disabled="loading || (selfService && !password)" @click="submit">
                        {{ $t('totp_reset') }}
                    </ActionBtn>
                </v-card-actions>
                <v-fade-transition>
                    <v-card-text v-if="error">
                        <v-alert type="error">{{ error }}</v-alert>
                    </v-card-text>
                </v-fade-transition>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { instance as api } from '@/helpers/api';
import { errToStr } from '@/helpers/tools';

/**
 * Shows whether a user has a second factor and lets it be cleared, so that the next login
 * starts the TOTP onboarding again.
 *
 * Two uses:
 * - selfService: the logged in user in his own profile. He has to confirm with his current
 *   password, so a hijacked session cannot move the second factor to another device.
 * - otherwise: an admin in the user administration (admins only, checked in the backend).
 *   The password of that user stays untouched.
 *
 * Emits 'reset' after a successful reset, so the parent can refresh its record.
 */
export default {
    name: 'TotpReset',
    props: {
        /** the totp_activated value of the user record (dayjs, invalid if not configured) */
        totpActivated: {
            type: Object,
            default: null,
        },
        /** the user to reset. Not needed in self service, the backend takes the session user. */
        userId: {
            type: Number,
            default: null,
        },
        /** true in the own profile, false in the user administration */
        selfService: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showDialog: false,
            password: '',
            loading: false,
            error: null,
            /**
             * Set after a successful reset. The status is shown from here instead of writing
             * the record: the reset already happened on the server, and touching the record
             * would mark it as changed although nothing has to be saved.
             */
            wasReset: false,
        };
    },
    computed: {
        isConfigured() {
            return !this.wasReset && !!this.totpActivated && this.totpActivated.isValid();
        },
        statusText() {
            if (!this.isConfigured) {
                return this.$t('totp_status_not_configured');
            }
            return this.$t('totp_status_configured', {
                date: this.totpActivated.formatIf('DD.MM.YYYY HH:mm'),
            });
        },
    },
    methods: {
        openDialog() {
            this.password = '';
            this.error = null;
            this.showDialog = true;
        },
        async submit() {
            if (this.loading || (this.selfService && !this.password)) {
                return;
            }
            this.loading = true;
            this.error = null;
            try {
                if (this.selfService) {
                    await api().post('/User/resetOwnTotp', { password: this.password });
                } else {
                    await api().post('/User/resetTotp', { id: this.userId });
                }
                this.showDialog = false;
                this.password = '';
                this.wasReset = true;
                this.$toast({ message: this.$t('totp_reset_success'), color: 'success' });
                this.$emit('reset');
            } catch (e) {
                this.error = errToStr(e);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
